import { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import PropertyFilter from './PropertyFilter';
import { FaMapMarkerAlt, FaBuilding, FaLayerGroup, FaHome, FaDice, FaCrown, FaArrowUp, FaEye, FaStar, FaShieldAlt, FaExpand, FaTimes, FaRegHeart, FaSearch } from 'react-icons/fa';

export default function Residential() {
  const navigate = useNavigate();
  const [filteredProperties, setFilteredProperties] = useState([]);
  const [visibleCards, setVisibleCards] = useState(new Set());
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [headerVisible, setHeaderVisible] = useState(false);
  const [loadedImages, setLoadedImages] = useState(new Set());
  const [hoveredCard, setHoveredCard] = useState(null);
  const cardRefs = useRef({});
  const headerRef = useRef(null);
  const statsRef = useRef(null);

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, []);

  // IntersectionObserver for scroll-reveal animations
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleCards((prev) => new Set([...prev, entry.target.dataset.cardId]));
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -60px 0px' }
    );
    Object.values(cardRefs.current).forEach((ref) => {
      if (ref) observer.observe(ref);
    });
    return () => observer.disconnect();
  }, [filteredProperties]);

  // Header reveal observer
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setHeaderVisible(true); }, { threshold: 0.2 });
    if (headerRef.current) obs.observe(headerRef.current);
    return () => obs.disconnect();
  }, []);

  // Show/hide scroll-to-top button
  useEffect(() => {
    const onScroll = () => setShowScrollTop(window.scrollY > 500);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const scrollToTop = useCallback(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const handleImageLoad = useCallback((id) => {
    setLoadedImages((prev) => new Set([...prev, id]));
  }, []);

  // Property data
  const properties = [
    {
      id: 1,
      slug: 'tonino-lamborghini-al-marjan',
      title: 'Tonino Lamborghini Residences',
      location: 'Al Marjan Island, Ras Al Khaimah, UAE',
      type: 'Apartments | Villas | Mansions',
      builder: 'BNW Developments',
      floors: 'G + 15 Floors',
      category: 'Residential',
      image: '/assets/residential/tonino-lamborghini-al-marjan/main.jpg',
      logo: '/assets/logos/bnw-developments-logo.png',
      isFeatured: true,
      details: {
        startingPrice: 'AED 1.75M',
        mapUrl: 'https://maps.app.goo.gl/YJyshFcpF7zRhLJL8?g_st=iw'
      }
    },
    {
      id: 101,
      slug: 'symphony-meydan-horizon',
      title: 'The Symphony by Imtiaz',
      location: 'Meydan Horizon, Dubai, UAE',
      type: 'Studio, 1–3 BR Apartments & Penthouses',
      builder: 'Imtiaz Developments',
      floors: '40-Storey Tower',
      category: 'Residential',
      image: '/assets/residential/imtiaz/symphony-meydan.jpg',
      logo: '/assets/logos/imtiaz-developments-logo.png',
      isFeatured: true,
      isLuxury: true,
      details: {
        startingPrice: 'AED 674K',
        architect: 'Zaha Hadid Architects',
        completion: 'Q2 2029'
      }
    },
    {
      id: 2,
      slug: 'the-hillgate-dubai-silicon-oasis',
      title: 'The Hillgate',
      location: 'Dubai Silicon Oasis, Dubai, UAE',
      type: '2 & 3 BR Apartments',
      builder: 'Ellington Properties',
      floors: 'Mid-Rise',
      category: 'Residential',
      image: '/assets/residential/ellington/the-hillgate.jpg',
      logo: '/assets/logos/ellington-properties-logo.png',
      isFeatured: false,
      details: {
        startingPrice: 'AED 1.94M'
      }
    },
    {
      id: 3,
      slug: 'riverton-house-meydan-horizon',
      title: 'Riverton House',
      location: 'Meydan Horizon, Dubai, UAE',
      type: '1 & 2 BR Residences',
      builder: 'Ellington Properties',
      floors: 'High-Rise',
      category: 'Residential',
      image: '/assets/residential/ellington/riverton-house.jpg',
      logo: '/assets/logos/ellington-properties-logo.png',
      isFeatured: false,
      details: {
        startingPrice: 'AED 2.17M'
      }
    },
    {
      id: 4,
      slug: 'belgravia-gardens-dubailand',
      title: 'Belgravia Gardens',
      location: 'Dubailand, Dubai, UAE',
      type: '2 & 3 BR Homes',
      builder: 'Ellington Properties',
      floors: 'Low-Rise',
      category: 'Residential',
      image: '/assets/residential/ellington/belgravia-gardens.jpg',
      logo: '/assets/logos/ellington-properties-logo.png',
      isFeatured: false,
      details: {
        startingPrice: 'AED 2.45M'
      }
    },
    {
      id: 5,
      slug: 'one-river-point-business-bay',
      title: 'One River Point & The Crestmark',
      location: 'Business Bay, Dubai, UAE',
      type: '1–4 BR Units',
      builder: 'Ellington Properties',
      floors: 'High-Rise Towers',
      category: 'Residential',
      image: '/assets/residential/ellington/one-river-point.jpg',
      logo: '/assets/logos/ellington-properties-logo.png',
      isFeatured: false,
      details: {
        startingPrice: 'AED 3.06M'
      }
    },
    {
      id: 6,
      slug: 'ocean-house-palm-jumeirah',
      title: 'Ocean House',
      location: 'Palm Jumeirah, Dubai, UAE',
      type: '2 & 4 BR Ultra-Luxury Residences',
      builder: 'Ellington Properties',
      floors: 'Exclusive Tower',
      category: 'Residential',
      image: '/assets/residential/ellington/ocean-house.jpg',
      logo: '/assets/logos/ellington-properties-logo.png',
      isFeatured: true,
      isLuxury: true,
      details: {
        startingPrice: 'AED 22.6M'
      }
    },
    {
      id: 7,
      slug: 'playa-del-sol-ras-al-khaimah',
      title: 'Playa Del Sol',
      location: 'Ras Al Khaimah, UAE',
      type: '2 & 3R Beachfront Homes',
      builder: 'Ellington Properties',
      floors: 'Beachfront Development',
      category: 'Residential',
      image: '/assets/residential/ellington/playa-del-sol.jpg',
      logo: '/assets/logos/ellington-properties-logo.png',
      isFeatured: false,
      details: {
        startingPrice: 'AED 3.8M'
      }
    },
    {
      id: 8,
      slug: 'costa-mare-al-marjan',
      title: 'Costa Mare',
      location: 'Al Marjan Island, Ras Al Khaimah, UAE',
      type: '1, 2 & 3 BR Beachfront Apartments',
      builder: 'Ellington Properties',
      floors: '4 Towers',
      category: 'Residential',
      image: '/assets/residential/ellington/costa-mare.jpg',
      logo: '/assets/logos/ellington-properties-logo.png',
      isFeatured: true,
      details: {
        startingPrice: 'AED 2.1M',
        wynn: true
      }
    },
    {
      id: 9,
      slug: 'eltiera-views-jumeirah-islands',
      title: 'Eltiera Views',
      location: 'Jumeirah Islands, Dubai, UAE',
      type: '1, 2 & 3 BR Apartments',
      builder: 'Ellington Properties',
      floors: '4 Towers | 26 Floors',
      category: 'Residential',
      image: '/assets/residential/ellington/eltiera-views.jpg',
      logo: '/assets/logos/ellington-properties-logo.png',
      isFeatured: false,
      details: {
        startingPrice: 'AED 1.5M'
      }
    },
    {
      id: 10,
      slug: 'mercer-house-uptown-dubai',
      title: 'Mercer House',
      location: 'Uptown Dubai, JLT, UAE',
      type: '1, 2 & 3 BR Apartments',
      builder: 'Ellington Properties',
      floors: '2 Towers',
      category: 'Residential',
      image: '/assets/residential/ellington/mercer-house.jpg',
      logo: '/assets/logos/ellington-properties-logo.png',
      isFeatured: false,
      details: {
        startingPrice: 'AED 4.4M'
      }
    },
    {
      id: 11,
      slug: 'portside-square-mina-rashid',
      title: 'Portside Square',
      location: 'Mina Rashid, Dubai, UAE',
      type: '1 & 2 BR Waterfront Apartments',
      builder: 'Ellington Properties',
      floors: '3 Towers | G+P+R+16',
      category: 'Residential',
      image: '/assets/residential/ellington/portside-square.jpg',
      logo: '/assets/logos/ellington-properties-logo.png',
      isFeatured: false,
      details: {
        startingPrice: 'AED 1.3M'
      }
    },
    {
      id: 12,
      slug: 'the-quayside-business-bay',
      title: 'The Quayside',
      location: 'Business Bay, Dubai, UAE',
      type: '2 BR Waterfront Apartments',
      builder: 'Ellington Properties',
      floors: 'High-Rise',
      category: 'Residential',
      image: '/assets/residential/ellington/the-quayside.jpg',
      logo: '/assets/logos/ellington-properties-logo.png',
      isFeatured: false,
      details: {
        startingPrice: 'AED 3.29M'
      }
    },
    {
      id: 13,
      slug: 'v-suites-business-bay',
      title: 'V-Suites',
      location: 'Business Bay, Dubai, UAE',
      type: 'Studios & 2BR Apartments',
      builder: 'ANAX Developments',
      floors: 'G+P+3+19 Floors',
      category: 'Residential',
      image: '/assets/residential/ellington/v-suites.jpg',
      logo: '/assets/logos/anax-developments-logo.png',
      isFeatured: false,
      details: {
        startingPrice: 'AED 1.62M'
      }
    },
    {
      id: 15,
      slug: 'taj-wellington-mews-al-marjan',
      title: 'Taj Wellington Mews',
      location: 'Al Marjan Island, Ras Al Khaimah, UAE',
      type: '1BR Luxury Apartments',
      builder: 'BNW Developments',
      floors: '2 Twin Towers',
      category: 'Residential',
      image: '/assets/residential/ellington/taj-wellington.jpg',
      logo: '/assets/logos/bnw-developments-logo.png',
      isFeatured: false,
      details: {
        startingPrice: 'AED 3.16M'
      }
    },
    {
      id: 16,
      slug: 'ramada-residences-al-jaddaf',
      title: 'Ramada Residences by Wyndham',
      location: 'Al Jaddaf, Dubai, UAE',
      type: '1 & 2 BR Branded Apartments',
      builder: 'BNW Developments & Wyndham',
      floors: 'High-Rise',
      category: 'Residential',
      image: '/assets/residential/ellington/al-jaddaf.jpg',
      logo: '/assets/logos/bnw-developments-logo.png',
      isFeatured: true,
      details: {
        startingPrice: 'AED 1.75M'
      }
    },
    {
      id: 17,
      slug: 'pelagia-al-marjan',
      title: 'Pelagia',
      location: 'Al Marjan Island, Ras Al Khaimah, UAE',
      type: '1-2-3-4 BR Waterfront Apartments',
      builder: 'BNW Developments',
      floors: 'Waterfront Complex',
      category: 'Residential',
      image: '/assets/residential/ellington/pelagia.jpg',
      logo: '/assets/logos/bnw-developments-logo.png',
      isFeatured: true,
      isLuxury: false,
      details: {
        startingPrice: 'AED 2.3M'
      }
    },
    {
      id: 18,
      slug: 'la-perla-al-marjan',
      title: 'La Perla',
      location: 'Al Marjan Island, Ras Al Khaimah, UAE',
      type: '1-2-3 BR Luxury Apartments',
      builder: 'BNW Developments',
      floors: 'Ground + 1-2-3-4-6 Floors',
      category: 'Residential',
      image: '/assets/residential/ellington/la-perla.jpg',
      logo: '/assets/logos/bnw-developments-logo.png',
      isFeatured: true,
      isLuxury: true,
      details: {
        startingPrice: 'AED 2.3M'
      }
    },
    {
      id: 19,
      slug: 'fashion-tv-acacia-al-marjan',
      title: 'Fashion TV Acacia',
      location: 'Al Marjan Island, Ras Al Khaimah, UAE',
      type: '1-2-3 BR & Penthouses',
      builder: 'BNW Developments',
      floors: 'Ground + 1-2-3-4-5-6 Floors',
      category: 'Residential',
      image: '/assets/residential/ellington/fashion-tv-acacia.jpg',
      logo: '/assets/logos/bnw-developments-logo.png',
      isFeatured: true,
      details: {
        startingPrice: 'AED 2M'
      }
    },
    {
      id: 20,
      slug: 'aqua-arc-al-marjan',
      title: 'Aqua Arc',
      location: 'Al Marjan Island, Ras Al Khaimah, UAE',
      type: '1-2-3BR Waterfront Apartments',
      builder: 'BNW Developments',
      floors: 'Floors 1-12',
      category: 'Residential',
      image: '/assets/residential/ellington/aqua-arc.jpg',
      logo: '/assets/logos/bnw-developments-logo.png',
      isFeatured: false,
      details: {
        startingPrice: 'AED 2.5M'
      }
    },
    {
      id: 21,
      slug: 'green-ville-emaar',
      title: 'Green Ville',
      location: 'Dubai, UAE',
      type: '1, 2 & 3 BR Apartments',
      builder: 'Emaar',
      floors: 'Mid-Rise',
      category: 'Residential',
      image: '/assets/residential/emaar/green-ville.jpg',
      logo: '/assets/logos/emaar-logo.png',
      isFeatured: false,
      details: {
        startingPrice: 'AED 1.5M'
      }
    },
    {
      id: 27,
      slug: 'bluewaters-residences-meraas',
      title: 'Bluewaters Residences',
      location: 'Bluewaters Island, Dubai, UAE',
      type: '1, 2, 3 & 4 BR Apartments',
      builder: 'Meraas',
      floors: '10 Apartment Buildings',
      category: 'Residential',
      image: '/assets/residential/meraas/bluewaters-residences.jpg',
      logo: '/assets/logos/meraas-logo.png',
      isFeatured: true,
      details: {
        startingPrice: 'AED 2.5M'
      }
    },
    {
      id: 35,
      slug: 'sobha-one-hartland',
      title: 'Sobha One',
      location: 'Sobha Hartland, Dubai, UAE',
      type: '1, 2, 3 & 4 BR Apartments',
      builder: 'Sobha Realty',
      floors: '5 Interconnected Towers',
      category: 'Residential',
      image: '/assets/residential/sobha/sobha-one.jpg',
      logo: '/assets/logos/sobha-realty-logo.png',
      isFeatured: true,
      details: {
        startingPrice: 'AED 1.8M'
      }
    },
    {
      id: 43,
      slug: 'bay-grove-nakheel',
      title: 'Bay Grove',
      location: 'Dubai Islands, Dubai, UAE',
      type: '1, 2 & 3 BR Apartments',
      builder: 'Nakheel',
      floors: 'Mid-Rise',
      category: 'Residential',
      image: '/assets/residential/nakheel/bay-grove.jpg',
      logo: '/assets/logos/nakheel-logo.png',
      isFeatured: true,
      details: {
        startingPrice: 'AED 1.2M'
      }
    },
    {
      id: 44,
      slug: 'bay-villas-nakheel',
      title: 'Bay Villas',
      location: 'Dubai Islands, Dubai, UAE',
      type: '3 & 4BR Beachfront Villas',
      builder: 'Nakheel',
      floors: 'Beachfront Villa Community',
      category: 'Residential',
      image: '/assets/residential/nakheel/bay-villas.jpg',
      logo: '/assets/logos/nakheel-logo.png',
      isFeatured: true,
      isLuxury: true,
      details: {
        startingPrice: 'AED 8M'
      }
    },
    {
      id: 45,
      slug: 'palm-jebel-ali-nakheel',
      title: 'Palm Jebel Ali',
      location: 'Palm Jebel Ali, Dubai, UAE',
      type: 'Luxury Villas & Apartments',
      builder: 'Nakheel',
      floors: 'Master Development',
      category: 'Residential',
      image: '/assets/residential/nakheel/palm-jebel-ali.jpg',
      logo: '/assets/logos/nakheel-logo.png',
      isFeatured: true,
      isLuxury: true,
      details: {
        startingPrice: 'AED 5M'
      }
    },
    {
      id: 46,
      slug: 'como-residences-nakheel',
      title: 'COMO Residences',
      location: 'Palm Jumeirah, Dubai, UAE',
      type: '2-7 BR Ultra-Luxury Residences',
      builder: 'Nakheel',
      floors: 'Ultra-Luxury Tower',
      category: 'Residential',
      image: '/assets/residential/nakheel/como-residences.jpg',
      logo: '/assets/logos/nakheel-logo.png',
      isFeatured: true,
      isLuxury: true,
      details: {
        startingPrice: 'AED 40M'
      }
    },
    {
      id: 47,
      slug: 'rixos-residences-nakheel',
      title: 'Rixos Residences',
      location: 'Dubai Islands, Dubai, UAE',
      type: 'Branded Residences & Hotel',
      builder: 'Nakheel',
      floors: 'High-Rise',
      category: 'Residential',
      image: '/assets/residential/nakheel/rixos.jpg',
      logo: '/assets/logos/nakheel-logo.png',
      isFeatured: true,
      isLuxury: true,
      details: {
        startingPrice: 'AED 3.5M'
      }
    },
    {
      id: 48,
      slug: 'palm-beach-towers-nakheel',
      title: 'Palm Beach Towers',
      location: 'Palm Jumeirah, Dubai, UAE',
      type: '1, 2, 3 & 4 BR Apartments',
      builder: 'Nakheel',
      floors: '3 Towers',
      category: 'Residential',
      image: '/assets/residential/nakheel/palm-beach-towers.jpg',
      logo: '/assets/logos/nakheel-logo.png',
      isFeatured: true,
      details: {
        startingPrice: 'AED 2.8M'
      }
    },
    {
      id: 49,
      slug: 'jebel-ali-village-nakheel',
      title: 'Jebel Ali Village',
      location: 'Jebel Ali, Dubai, UAE',
      type: 'Villas & Townhouses',
      builder: 'Nakheel',
      floors: 'Villa Community',
      category: 'Residential',
      image: '/assets/residential/nakheel/jebel-ali-village.jpg',
      logo: '/assets/logos/nakheel-logo.png',
      isFeatured: false,
      details: {
        startingPrice: 'AED 2.5M'
      }
    },
    {
      id: 50,
      slug: 'tilal-nakheel',
      title: 'Tilal',
      location: 'Dubai Islands, Dubai, UAE',
      type: '1, 2 & 3 BR Apartments',
      builder: 'Nakheel',
      floors: 'Mid-Rise',
      category: 'Residential',
      image: '/assets/residential/nakheel/tilal.jpg',
      logo: '/assets/logos/nakheel-logo.png',
      isFeatured: false,
      details: {
        startingPrice: 'AED 1.1M'
      }
    },
    {
      id: 52,
      slug: 'binghatti-skyrise-business-bay',
      title: 'Binghatti Skyrise',
      location: 'Business Bay, Dubai, UAE',
      type: 'Studios, 1, 2 & 3 BR Apartments',
      builder: 'Binghatti',
      floors: 'High-Rise Tower',
      category: 'Residential',
      image: '/assets/residential/binghatti/binghatti-skyrise.jpg',
      logo: '/assets/logos/binghatti-logo.png',
      isFeatured: true,
      details: {
        startingPrice: 'AED 850K'
      }
    },
    {
      id: 53,
      slug: 'danube-elitz-3-al-furjan',
      title: 'Danube Elitz 3',
      location: 'Al Furjan, Dubai, UAE',
      type: 'Studios, 1 & 2 BR Apartments',
      builder: 'Danube Properties',
      floors: 'High-Rise',
      category: 'Residential',
      image: '/assets/residential/danube/danube-elitz-3.jpg',
      logo: '/assets/logos/danube-logo.png',
      isFeatured: true,
      details: {
        startingPrice: 'AED 499K'
      }
    },
    {
      id: 54,
      slug: 'peace-homes-town-square',
      title: 'Peace Homes',
      location: 'Town Square, Dubai, UAE',
      type: '1, 2 & 3 BR Apartments',
      builder: 'Peace Homes',
      floors: 'Mid-Rise',
      category: 'Residential',
      image: '/assets/residential/peace-homes/peace-homes-town-square.jpg',
      logo: '/assets/logos/peace-homes-logo.png',
      isFeatured: false,
      details: {
        startingPrice: 'AED 650K'
      }
    },
    {
      id: 55,
      slug: 'jacob-and-co-residences',
      title: 'Jacob & Co Residences',
      location: 'Business Bay, Dubai, UAE',
      type: 'Luxury Branded Residences',
      builder: 'Jacob & Co',
      floors: 'Ultra-Luxury Tower',
      category: 'Residential',
      image: '/assets/residential/jacob-co/jacob-co-residences.jpg',
      logo: '/assets/logos/jacob-co-logo.png',
      isFeatured: true,
      isLuxury: true,
      details: {
        startingPrice: 'AED 20M'
      }
    },
    {
      id: 56,
      slug: 'samana-park-views-arjan',
      title: 'Samana Park Views',
      location: 'Arjan, Dubai, UAE',
      type: 'Studios, 1 & 2 BR Apartments',
      builder: 'Samana Developers',
      floors: 'Mid-Rise',
      category: 'Residential',
      image: '/assets/residential/samana/samana-park-views.jpg',
      logo: '/assets/logos/samana-logo.png',
      isFeatured: false,
      details: {
        startingPrice: 'AED 380K'
      }
    },
    {
      id: 57,
      slug: 'one-broker-heights-jvc',
      title: 'One Broker Heights',
      location: 'JVC, Dubai, UAE',
      type: '1 & 2 BR Apartments',
      builder: 'One Broker Group',
      floors: 'Mid-Rise',
      category: 'Residential',
      image: '/assets/residential/one-broker/one-broker-heights.jpg',
      logo: '/assets/logos/one-broker-logo.png',
      isFeatured: false,
      details: {
        startingPrice: 'AED 550K'
      }
    },
    {
      id: 58,
      slug: 'difc-living-difc',
      title: 'DIFC Living',
      location: 'DIFC, Dubai, UAE',
      type: '1, 2 & 3 BR Luxury Apartments',
      builder: 'DIFC',
      floors: 'High-Rise',
      category: 'Residential',
      image: '/assets/residential/difc/difc-living.jpg',
      logo: '/assets/logos/difc-logo.png',
      isFeatured: true,
      details: {
        startingPrice: 'AED 2.5M'
      }
    },
    {
      id: 59,
      slug: 'damac-lagoons-damac-hills',
      title: 'DAMAC Lagoons',
      location: 'DAMAC Hills, Dubai, UAE',
      type: '3-6 BR Luxury Villas',
      builder: 'DAMAC Properties',
      floors: 'Villa Community',
      category: 'Residential',
      image: '/assets/residential/damac/damac-lagoons.jpg',
      logo: '/assets/logos/damac-logo.png',
      isFeatured: true,
      isLuxury: true,
      details: {
        startingPrice: 'AED 2.8M'
      }
    },
    {
      id: 60,
      slug: 'district-one-meydan',
      title: 'District One',
      location: 'Meydan, Dubai, UAE',
      type: 'Ultra-Luxury Villas',
      builder: 'Dubai Properties',
      floors: 'Luxury Villa Community',
      category: 'Residential',
      image: '/assets/residential/dubai-properties/district-one.jpg',
      logo: '/assets/logos/dubai-properties-logo.png',
      isFeatured: true,
      isLuxury: true,
      details: {
        startingPrice: 'AED 15M'
      }
    },
    {
      id: 61,
      slug: 'select-group-grand-bleu',
      title: 'Grand Bleu Tower',
      location: 'Emaar Beachfront, Dubai, UAE',
      type: '2-4 BR Beachfront Apartments',
      builder: 'Select Group',
      floors: 'High-Rise',
      category: 'Residential',
      image: '/assets/residential/select-group/grand-bleu.jpg',
      logo: '/assets/logos/select-group-logo.png',
      isFeatured: true,
      details: {
        startingPrice: 'AED 4.5M'
      }
    },
    {
      id: 62,
      slug: 'the-opus-business-bay',
      title: 'The Opus',
      location: 'Business Bay, Dubai, UAE',
      type: 'Ultra-Luxury Serviced Residences',
      builder: 'Omniyat',
      floors: 'Iconic Tower',
      category: 'Residential',
      image: '/assets/residential/omniyat/the-opus.jpg',
      logo: '/assets/logos/omniyat-logo.png',
      isFeatured: true,
      isLuxury: true,
      details: {
        startingPrice: 'AED 8M'
      }
    },
    {
      id: 63,
      slug: 'deyaar-midtown-dubai-production-city',
      title: 'Midtown',
      location: 'Dubai Production City, Dubai, UAE',
      type: 'Studios, 1 & 2 BR Apartments',
      builder: 'Deyaar',
      floors: 'Mid-Rise',
      category: 'Residential',
      image: '/assets/residential/deyaar/midtown.jpg',
      logo: '/assets/logos/deyaar-logo.png',
      isFeatured: false,
      details: {
        startingPrice: 'AED 450K'
      }
    },
    {
      id: 64,
      slug: 'majid-al-futtaim-tilal-al-ghaf',
      title: 'Tilal Al Ghaf',
      location: 'Dubai, UAE',
      type: 'Villas & Townhouses',
      builder: 'Majid Al Futtaim',
      floors: 'Master Community',
      category: 'Residential',
      image: '/assets/residential/maf/tilal-al-ghaf.jpg',
      logo: '/assets/logos/maf-logo.png',
      isFeatured: true,
      details: {
        startingPrice: 'AED 3.2M'
      }
    },
    {
      id: 65,
      slug: 'nshama-town-square',
      title: 'Town Square Dubai',
      location: 'Town Square, Dubai, UAE',
      type: 'Apartments & Townhouses',
      builder: 'Nshama',
      floors: 'Master Community',
      category: 'Residential',
      image: '/assets/residential/nshama/town-square.jpg',
      logo: '/assets/logos/nshama-logo.png',
      isFeatured: true,
      details: {
        startingPrice: 'AED 750K'
      }
    },
    {
      id: 66,
      slug: 'tiger-group-dubai-hills',
      title: 'Tiger Properties Dubai Hills',
      location: 'Dubai Hills Estate, Dubai, UAE',
      type: '1, 2 & 3 BR Apartments',
      builder: 'Tiger Group',
      floors: 'Mid-Rise',
      category: 'Residential',
      image: '/assets/residential/tiger/dubai-hills.jpg',
      logo: '/assets/logos/tiger-logo.png',
      isFeatured: false,
      details: {
        startingPrice: 'AED 1.2M'
      }
    }
  ];

  // Initialize filtered properties on mount
  useEffect(() => {
    setFilteredProperties(properties);
  }, []);

  const handleFilterChange = (filtered) => {
    setFilteredProperties(filtered);
  };

  const handleContactClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    navigate('/contact');
  };

  const handleLocationClick = (e, mapUrl) => {
    e.preventDefault();
    e.stopPropagation();
    window.open(mapUrl, '_blank', 'noopener,noreferrer');
  };

  const [quickViewProperty, setQuickViewProperty] = useState(null);

  const handleQuickView = (e, property) => {
    e.preventDefault();
    e.stopPropagation();
    setQuickViewProperty(property);
    document.body.style.overflow = 'hidden';
  };

  const closeQuickView = () => {
    setQuickViewProperty(null);
    document.body.style.overflow = 'auto';
  };

  return (
    <>
      {/* ── Main Section ──────────────────────────────── */}
      <section className="relative pt-28 pb-20 bg-[#fafafa] min-h-screen w-full overflow-x-hidden">
        {/* Top gold gradient accent bar */}
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-[#c8a45e] to-transparent z-10" />

        {/* Animated background particles */}
        <div className="absolute inset-0 pointer-events-none z-0 overflow-hidden">
          <div className="absolute w-[500px] h-[500px] rounded-full opacity-[0.03] bg-[#c8a45e] -top-60 -left-60 animate-pulse" style={{ animationDuration: '6s' }} />
          <div className="absolute w-[400px] h-[400px] rounded-full opacity-[0.02] bg-[#c8a45e] top-1/3 -right-40 animate-pulse" style={{ animationDuration: '8s' }} />
          <div className="absolute w-[300px] h-[300px] rounded-full opacity-[0.025] bg-[#c8a45e] bottom-20 left-1/4 animate-pulse" style={{ animationDuration: '10s' }} />
        </div>

        <div className="relative z-[1] max-w-[1440px] mx-auto px-5 sm:px-6 lg:px-10">
          {/* ── Header with scroll-triggered reveal ──── */}
          <div
            ref={headerRef}
            className={`text-center mb-14 transition-all duration-1000 ease-out ${headerVisible
              ? 'opacity-100 translate-y-0 blur-0'
              : 'opacity-0 translate-y-8 blur-[4px]'
              }`}
          >
            {/* Mini label */}
            <div className={`inline-flex items-center gap-2 bg-[#0a0a0a] text-white px-4 py-1.5 rounded-full text-[0.65rem] font-display tracking-[2px] uppercase mb-5 transition-all duration-700 delay-200 ${headerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
              <FaStar className="text-[#c8a45e] text-[0.6rem]" />
              <span>Premium Collection</span>
              <FaStar className="text-[#c8a45e] text-[0.6rem]" />
            </div>

            <h2 className="font-display text-[clamp(1.8rem,4vw,3.5rem)] text-[#0a0a0a] tracking-[1px] uppercase font-bold mb-4 relative inline-block">
              Latest <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#c8a45e] to-[#a07d3a]">Residential</span> Properties
              <span className="absolute -bottom-4 left-1/2 -translate-x-1/2 w-24 h-[3px] bg-gradient-to-r from-transparent via-[#c8a45e] to-transparent rounded-full" />
            </h2>
            <p className="font-body text-[clamp(0.95rem,1.5vw,1.15rem)] text-gray-500 leading-relaxed mt-6 max-w-xl mx-auto tracking-wide">
              Discover our curated selection of luxury homes, apartments, and villas in Dubai's most prestigious locations.
            </p>
          </div>

          {/* ── Stats Bar ─────────────────────────────── */}
          <div ref={statsRef} className={`flex flex-wrap justify-center gap-4 sm:gap-6 mb-12 transition-all duration-700 delay-300 ${headerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
            {[
              { label: 'Properties', value: properties.length, icon: <FaHome /> },
              { label: 'Luxury', value: properties.filter(p => p.isLuxury).length, icon: <FaCrown /> },
              { label: 'Featured', value: properties.filter(p => p.isFeatured).length, icon: <FaStar /> },
              { label: 'Developers', value: [...new Set(properties.map(p => p.builder))].length, icon: <FaShieldAlt /> },
            ].map((s, i) => (
              <div key={i} className="flex items-center gap-3 bg-white border border-gray-100 rounded-2xl px-6 py-4 shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all duration-300 cursor-default min-w-[160px]">
                <div className="w-10 h-10 rounded-full bg-[#c8a45e]/10 flex items-center justify-center text-[#c8a45e] text-lg">
                  {s.icon}
                </div>
                <div className="flex flex-col">
                  <span className="font-display text-xl font-bold text-[#0a0a0a] leading-none">{s.value}</span>
                  <span className="font-body text-[0.65rem] text-gray-400 uppercase tracking-[1.5px] mt-1">{s.label}</span>
                </div>
              </div>
            ))}
          </div>

          {/* Property Filter */}
          {/* Property Filter */}
          <div className="sticky top-24 z-30 mb-10 transition-all duration-300">
            <PropertyFilter properties={properties} onFilterChange={handleFilterChange} />
          </div>

          {/* Results Count */}
          <div className={`flex justify-between items-center mb-8 px-2 transition-all duration-500 delay-500 ${headerVisible ? 'opacity-100' : 'opacity-0'}`}>
            <p className="font-body text-sm text-gray-500">
              Showing <strong className="text-[#0a0a0a] font-bold">{filteredProperties.length}</strong> results
            </p>
            <div className="text-xs font-display uppercase tracking-widest text-[#c8a45e] font-bold">
              {filteredProperties.length === 0 ? 'No Matches' : 'Premium Selection'}
            </div>
          </div>

          {/* ── Property Grid ─────────────────────────── */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 gap-8">
            {filteredProperties.length > 0 ? (
              filteredProperties.map((property, index) => {
                const isVisible = visibleCards.has(String(property.id));
                const isImgLoaded = loadedImages.has(property.id);
                const isHovered = hoveredCard === property.id;

                return (
                  <div
                    key={property.id}
                    className="relative group perspective-1000"
                    onMouseEnter={() => setHoveredCard(property.id)}
                    onMouseLeave={() => setHoveredCard(null)}
                  >
                    <Link
                      to={`/properties/residential/${property.slug}`}
                      className="no-underline block h-full"
                      style={{ textDecoration: 'none' }}
                    >
                      <div
                        ref={(el) => { cardRefs.current[property.id] = el; }}
                        data-card-id={property.id}
                        className={`
                        relative flex flex-col h-full
                        bg-white rounded-[20px] overflow-hidden
                        transition-all duration-500 ease-out
                        hover:-translate-y-2 hover:shadow-[0_20px_50px_-12px_rgba(0,0,0,0.1)]
                        ${isVisible
                            ? 'opacity-100 translate-y-0'
                            : 'opacity-0 translate-y-12'}
                        ${property.isFeatured
                            ? 'ring-1 ring-[#c8a45e]/30'
                            : 'ring-1 ring-gray-100'}
                      `}
                        style={{ transitionDelay: isVisible ? '0ms' : `${(index % 6) * 100}ms` }}
                      >
                        {/* ── Image Section ───────────────── */}
                        <div className="relative w-full aspect-[4/3] overflow-hidden bg-gray-100">
                          {/* Skeleton loader */}
                          {!isImgLoaded && (
                            <div className="absolute inset-0 z-[5] bg-gradient-to-r from-gray-200 via-gray-100 to-gray-200 animate-shimmer" style={{ backgroundSize: '200% 100%' }} />
                          )}

                          <img
                            src={property.image}
                            alt={property.title}
                            onLoad={() => handleImageLoad(property.id)}
                            className={`w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-110 ${isImgLoaded ? 'opacity-100' : 'opacity-0'}`}
                          />

                          {/* Overlays */}
                          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-80" />

                          {/* Interactive Buttons Overlay */}
                          <div className="absolute top-4 right-4 flex flex-col gap-2 z-10 translate-x-12 opacity-0 group-hover:translate-x-0 group-hover:opacity-100 transition-all duration-300">
                            <button
                              onClick={(e) => { e.preventDefault(); /* Like logic */ }}
                              className="w-10 h-10 rounded-full bg-white/90 backdrop-blur text-gray-800 hover:text-red-500 hover:bg-white flex items-center justify-center shadow-lg transition-all"
                              title="Save to Favorites"
                            >
                              <FaRegHeart />
                            </button>
                            <button
                              onClick={(e) => handleQuickView(e, property)}
                              className="w-10 h-10 rounded-full bg-white/90 backdrop-blur text-gray-800 hover:text-[#c8a45e] hover:bg-white flex items-center justify-center shadow-lg transition-all delay-75"
                              title="Quick View"
                            >
                              <FaExpand />
                            </button>
                          </div>

                          {/* Badges */}
                          <div className="absolute top-4 left-4 flex flex-col gap-2 z-10">
                            {property.isLuxury && (
                              <span className="bg-[#0a0a0a]/90 backdrop-blur-md text-[#c8a45e] text-[0.65rem] font-bold uppercase tracking-wider px-3 py-1.5 rounded-lg border border-[#c8a45e]/20 shadow-lg">
                                Ultra Luxury
                              </span>
                            )}
                            {property.isFeatured && (
                              <span className="bg-white/90 backdrop-blur-md text-[#0a0a0a] text-[0.65rem] font-bold uppercase tracking-wider px-3 py-1.5 rounded-lg shadow-lg">
                                Featured
                              </span>
                            )}
                          </div>

                          {/* Price Tag - Bottom Left */}
                          <div className="absolute bottom-4 left-4 z-10">
                            <div className="bg-white/95 backdrop-blur-md px-4 py-2 rounded-xl shadow-lg border border-white/20">
                              <p className="text-[0.65rem] text-gray-500 font-bold uppercase tracking-wider mb-0.5">Starting From</p>
                              <p className="text-lg font-display font-bold text-[#0a0a0a]">
                                {property.details?.startingPrice || 'Price on Request'}
                              </p>
                            </div>
                          </div>

                          {/* Builder Logo - Bottom Right */}
                          <div className="absolute bottom-4 right-4 z-10 w-12 h-12 bg-white rounded-xl p-1.5 shadow-lg flex items-center justify-center">
                            <img src={property.logo} alt={property.builder} className="w-full h-full object-contain" />
                          </div>
                        </div>

                        {/* ── Content Section ───────────────── */}
                        <div className="p-6 flex flex-col flex-grow">
                          <div className="mb-4">
                            <div className="flex items-center gap-2 mb-2 text-[#c8a45e] text-xs font-bold uppercase tracking-wider">
                              <FaBuilding className="text-[0.7rem]" />
                              <span>{property.builder}</span>
                            </div>
                            <h3 className="font-display text-xl text-[#0a0a0a] font-bold leading-tight group-hover:text-[#c8a45e] transition-colors line-clamp-2">
                              {property.title}
                            </h3>
                            <div className="flex items-center gap-2 mt-2 text-gray-500 text-sm">
                              <FaMapMarkerAlt className="text-[#c8a45e] flex-shrink-0" />
                              <span className="truncate">{property.location}</span>
                            </div>
                          </div>

                          <div className="mt-auto pt-4 border-t border-dashed border-gray-200 grid grid-cols-2 gap-4">
                            <div className="flex items-center gap-2 text-gray-600 text-sm">
                              <FaHome className="text-gray-400" />
                              <span className="truncate max-w-full">{property.type.split('|')[0] || property.type}</span>
                            </div>
                            <div className="flex items-center gap-2 text-gray-600 text-sm">
                              <FaLayerGroup className="text-gray-400" />
                              <span className="truncate">{property.floors}</span>
                            </div>
                          </div>

                          <div className="mt-5 opacity-0 max-h-0 group-hover:opacity-100 group-hover:max-h-12 transition-all duration-300 overflow-hidden">
                            <button className="w-full py-2.5 bg-[#0a0a0a] text-white rounded-lg text-xs font-bold uppercase tracking-widest hover:bg-[#c8a45e] transition-colors">
                              View Details
                            </button>
                          </div>
                        </div>
                      </div>
                    </Link>
                  </div>
                );
              })
            ) : (
              <div className="col-span-full py-20 text-center">
                <div className="w-24 h-24 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-6">
                  <FaSearch className="text-3xl text-gray-300" />
                </div>
                <h3 className="text-2xl font-display font-bold text-[#0a0a0a] mb-2">No properties found</h3>
                <p className="text-gray-500">Try adjusting your filters to find what you're looking for.</p>
                <button
                  onClick={() => window.location.reload()} // Ideally reset filters
                  className="mt-6 px-8 py-3 bg-white border border-gray-200 rounded-full text-sm font-bold uppercase tracking-wider hover:bg-[#0a0a0a] hover:text-white transition-all shadow-sm"
                >
                  Clear Filters
                </button>
              </div>
            )}
          </div>

          {/* ── View All CTA ─────────────────────────── */}
          <div className="text-center mt-20">
            <div className="inline-block relative group">
              <div className="absolute inset-0 bg-[#c8a45e] blur-xl opacity-20 group-hover:opacity-40 transition-opacity duration-500"></div>
              <Link to="/contact">
                <button className="relative bg-[#0a0a0a] text-white px-10 py-4 rounded-full font-display text-sm font-bold uppercase tracking-[2px] hover:bg-white hover:text-[#0a0a0a] transition-all duration-300 border border-[#0a0a0a] shadow-2xl">
                  Contact Our Team
                </button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ── Quick View Modal ───────────────────────── */}
      {quickViewProperty && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-md animate-fade-in"
            onClick={closeQuickView}
          ></div>

          {/* Modal Content */}
          <div className="relative w-full max-w-4xl bg-white rounded-3xl overflow-hidden shadow-2xl animate-fade-in-up flex flex-col md:flex-row max-h-[90vh]">

            {/* Image Side */}
            <div className="w-full md:w-1/2 h-64 md:h-auto relative bg-gray-100">
              <img
                src={quickViewProperty.image}
                alt={quickViewProperty.title}
                className="w-full h-full object-cover"
              />
              <div className="absolute top-4 left-4">
                <span className="bg-white/90 backdrop-blur px-3 py-1 rounded-lg text-xs font-bold uppercase tracking-wider text-[#0a0a0a]">
                  {quickViewProperty.category}
                </span>
              </div>
            </div>

            {/* Content Side */}
            <div className="w-full md:w-1/2 p-8 md:p-10 flex flex-col overflow-y-auto">
              <button
                onClick={closeQuickView}
                className="absolute top-4 right-4 w-10 h-10 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center transition-colors text-gray-500"
              >
                <FaTimes />
              </button>

              <div className="mb-2 text-[#c8a45e] text-xs font-bold uppercase tracking-widest">
                {quickViewProperty.builder}
              </div>

              <h2 className="font-display text-2xl md:text-3xl font-bold text-[#0a0a0a] mb-2 leading-tight">
                {quickViewProperty.title}
              </h2>

              <div className="flex items-center gap-2 text-gray-500 text-sm mb-6">
                <FaMapMarkerAlt className="text-[#c8a45e]" />
                {quickViewProperty.location}
              </div>

              <div className="grid grid-cols-2 gap-4 mb-8">
                <div className="p-4 bg-gray-50 rounded-xl border border-gray-100">
                  <p className="text-xs text-gray-400 uppercase tracking-wider mb-1">Price</p>
                  <p className="font-display text-lg font-bold text-[#0a0a0a]">
                    {quickViewProperty.details?.startingPrice || 'On Request'}
                  </p>
                </div>
                <div className="p-4 bg-gray-50 rounded-xl border border-gray-100">
                  <p className="text-xs text-gray-400 uppercase tracking-wider mb-1">Type</p>
                  <p className="font-display text-sm font-bold text-[#0a0a0a] truncate">
                    {quickViewProperty.type.split(',')[0]}
                  </p>
                </div>
              </div>

              <p className="text-gray-600 mb-8 leading-relaxed text-sm">
                Experience luxury living at {quickViewProperty.title}. Located in the prime area of {quickViewProperty.location}, this property offers exceptional amenities and refined architecture by {quickViewProperty.builder}.
              </p>

              <div className="mt-auto flex flex-col gap-3">
                <Link
                  to={`/properties/residential/${quickViewProperty.slug}`}
                  className="w-full py-4 bg-[#0a0a0a] text-white rounded-xl text-center font-display text-sm font-bold uppercase tracking-widest hover:bg-[#333] transition-colors"
                >
                  View Full Details
                </Link>
                <Link
                  to="/contact"
                  className="w-full py-4 border-2 border-[#0a0a0a] text-[#0a0a0a] rounded-xl text-center font-display text-sm font-bold uppercase tracking-widest hover:bg-gray-50 transition-colors"
                >
                  Enquire Now
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* ── CTA Section ───────────────────────────────── */}
      <section className="relative bg-gradient-to-br from-[#0a0a0a] via-[#1a1a1a] to-[#0f0f0f] py-24 sm:py-32 text-center overflow-hidden">
        {/* Decorative lines */}
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#c8a45e] to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#c8a45e]/30 to-transparent" />

        {/* Animated radial glows */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <div className="absolute w-[600px] h-[600px] rounded-full bg-[#c8a45e]/[0.04] top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 animate-pulse" style={{ animationDuration: '5s' }} />
          <div className="absolute w-[300px] h-[300px] rounded-full bg-[#c8a45e]/[0.03] top-0 right-0 -translate-y-1/2 translate-x-1/3 animate-pulse" style={{ animationDuration: '7s' }} />
        </div>

        <div className="relative z-[1] max-w-3xl mx-auto px-5">
          <div className="inline-flex items-center gap-2 bg-white/5 backdrop-blur-sm text-[#c8a45e] px-4 py-1.5 rounded-full text-[0.65rem] font-display tracking-[2px] uppercase mb-6 border border-white/10">
            <FaStar className="text-[0.55rem]" />
            <span>Start Your Journey</span>
            <FaStar className="text-[0.55rem]" />
          </div>
          <h2 className="font-display text-[clamp(1.6rem,4vw,2.8rem)] text-white tracking-[2px] uppercase font-bold mb-4">
            Ready to Find Your Dream Home?
          </h2>
          <p className="font-body text-[clamp(1rem,1.5vw,1.2rem)] text-white/60 mb-10 leading-relaxed tracking-wide max-w-lg mx-auto">
            Connect with our property experts and discover exclusive listings tailored for you
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 px-10 py-[16px] bg-gradient-to-r from-[#c8a45e] to-[#d4a746] text-[#1a1a1a] rounded-full font-display text-sm font-bold tracking-[1.5px] uppercase no-underline transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_16px_48px_rgba(200,164,94,0.35)] active:-translate-y-0.5"
            >
              Get In Touch
            </Link>
            <Link
              to="/properties/residential"
              className="inline-flex items-center gap-2 px-10 py-[16px] bg-transparent text-white border-2 border-white/20 rounded-full font-display text-sm font-bold tracking-[1.5px] uppercase no-underline transition-all duration-300 hover:border-[#c8a45e] hover:text-[#c8a45e] hover:-translate-y-1 active:-translate-y-0.5"
            >
              Browse All
            </Link>
          </div>
        </div>
      </section>

      {/* ── Floating Back-to-Top Button ───────────────── */}
      <button
        onClick={scrollToTop}
        aria-label="Scroll to top"
        className={`fixed bottom-8 right-8 z-50 w-12 h-12 rounded-full bg-[#0a0a0a] text-white flex items-center justify-center shadow-[0_8px_32px_rgba(0,0,0,0.25)] border border-white/10 cursor-pointer transition-all duration-400 hover:bg-[#c8a45e] hover:text-[#1a1a1a] hover:-translate-y-1 hover:shadow-[0_12px_40px_rgba(200,164,94,0.3)] active:translate-y-0 ${showScrollTop ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-4 scale-75 pointer-events-none'}`}
      >
        <FaArrowUp className="text-sm" />
      </button>
    </>
  );
}
